import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { TreeControls } from "@common/treeControls";
import { Row } from "./row";
import { LoaderRow } from "./loader/row";
import {
  computeExpectedPeriods,
  formatPeriodLabel,
  groupPeriodsByMonth,
} from "./utils";
import { Props } from "./types";
import styles from "./styles.scss";

const FIRST_LOAD_ROWS = 8;
const LOADING_MORE_ROWS = 3;

function Table(props: Props) {
  const { t, i18n } = useTranslation();
  const periods =
    props.resources[0]?.periods ||
    computeExpectedPeriods(props.from, props.to, props.granularity);
  const isWeekly = props.granularity === "WEEK";
  const monthGroups = isWeekly
    ? groupPeriodsByMonth(periods, i18n.language)
    : [];

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.table}>
        {isWeekly && (
          <div className={styles.monthRow}>
            <div className={styles.monthCornerCell} />
            {monthGroups.map((group, index) => (
              <div
                key={index}
                className={styles.monthCell}
                style={{ width: `${group.span * 10}rem` }}
              >
                {group.label}
              </div>
            ))}
          </div>
        )}
        <div
          className={classNames(styles.headerRow, {
            [styles.withMonthRow]: isWeekly,
          })}
        >
          <div className={styles.resourceHeaderCell}>
            {t("occupancyPage.table.resource")}

            <TreeControls
              expandAllKey="occupancyPage.toolbar.expandAll"
              collapseAllKey="occupancyPage.toolbar.collapseAll"
              expandAll={props.expandAll}
              collapseAll={props.collapseAll}
            />
          </div>
          {periods.map((period, index) => (
            <div key={index} className={styles.headerCell}>
              {formatPeriodLabel(period, props.granularity, i18n.language)}
            </div>
          ))}
        </div>
        {props.resources.map((resource) => (
          <Row
            key={resource.uuid}
            resource={resource}
            locale={i18n.language}
            from={props.from}
            to={props.to}
            granularity={props.granularity}
            sort={props.sort}
            filter={props.filter}
            isExpanded={props.expandedResources.includes(resource.uuid)}
            toggle={() => props.toggleResource(resource.uuid)}
          />
        ))}
        {props.isLoadingMore &&
          Array.from(
            {
              length:
                props.resources.length === 0
                  ? FIRST_LOAD_ROWS
                  : LOADING_MORE_ROWS,
            },
            (_, index) => <LoaderRow key={index} columns={periods.length} />,
          )}
      </div>
    </div>
  );
}

export { Table };
